import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ScaleIcon,
  Square2StackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  EXACT_DUP_LIMIT_DEFAULT,
  type ExactDupApplyRequest,
  type ExactDupDecision,
  type ExactDupGroup,
  type ExactDupPath,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import { ApiError } from '../gallery/api';
import { interpolate } from '../gallery/format';
import { formatBytes } from '../viewer/format';
import { withSlotParam } from '../lib/activeSlot';
import { recordAction } from '../diagnostics/buffers';
import { Portal } from '../components/Portal';
import { useRefreshAfterJob } from '../notifications/use-refresh-after-job';
import { openViewerSeries } from '../viewer/ViewerRoute';
import { applyExact, fetchExactGroups, fetchExactSummary } from './dedup-api';
import { ExactConfirmModal } from './ExactConfirmModal';

/**
 * Section « COPIES IDENTIQUES » — un des DEUX onglets de la planche de dédoublonnage.
 *
 * Distincte de l'analyse des quasi-doublons (phash) : ici l'inventaire vient du SCAN
 * (`asset_file_paths`), il n'y a donc PAS d'analyse à lancer — juste « Actualiser ».
 * On liste les groupes de fichiers STRICTEMENT identiques (même identité pixels
 * présente à ≥ 2 chemins physiques), on laisse choisir LE chemin à garder par image,
 * puis on enfile la suppression des autres (`dedup_exact_apply`).
 *
 * GARDE-FOU « ≥ 1 chemin gardé par groupe » : la sélection d'un groupe est UNE valeur
 * — le `keepRelPath` choisi. Un radiogroup a toujours exactement une valeur → il n'existe
 * PAS d'état « 0 chemin gardé » représentable → la suppression totale d'un groupe est
 * STRUCTURELLEMENT impossible. Chaque groupe émet une décision `{identityHash, keepRelPath}`.
 *
 * DÉFAUT DU KEEPER (décision utilisateur) : les pixels étant identiques, on garde par
 * défaut le fichier le PLUS LÉGER (`sizeBytes` min), départage `isCanonical` → chemin le
 * plus court → lexicographique. La RAISON est affichée (icône + texte). L'utilisateur peut
 * toujours changer le keeper via les radios ; ce n'est QUE le défaut qui change.
 *
 * ÉTAT CONTRÔLÉ (page + choix) : quand `DedupReviewPage` monte cette section dans un onglet,
 * il PILOTE `page`/`choices` (props contrôlées) pour pouvoir les RESTAURER au retour de la
 * visionneuse (scroll-restore). En usage autonome (aucune prop fournie), la section garde
 * son propre état interne — rétro-compatible.
 */

interface Feedback {
  kind: 'applyRunning' | 'applyQueued' | 'applyError' | 'writeDisabled';
}

/** Choix par groupe : `identityHash` → `keepRelPath` du chemin conservé. */
export type ExactChoices = Map<string, string>;

/**
 * Raison du défaut de keeper d'un groupe. `lightest` = le fichier le plus léger a gagné ;
 * `canonical` = le départage par « copie indexée » a tranché (égalité de taille).
 */
export type ExactKeepReason = 'lightest' | 'canonical';

/** Le chemin gardé par défaut d'un groupe + la raison (pour l'afficher, jamais la couleur seule). */
export interface DefaultKeep {
  relPath: string;
  reason: ExactKeepReason;
}

/**
 * Choisit le chemin gardé par DÉFAUT d'un groupe de copies EXACTES (pixels identiques) :
 * le fichier le PLUS LÉGER (`sizeBytes` min) ; en cas d'ÉGALITÉ de taille, préférer la
 * copie `isCanonical` (indexée), puis le chemin (`relPath`) le plus COURT, puis l'ordre
 * lexicographique — 100 % déterministe et stable (ne dépend jamais de l'ordre d'entrée).
 *
 * PUR & testable. Un groupe anormalement vide (le contrat garantit `paths.min(2)`, mais on
 * ne suppose jamais) retombe sur `{ relPath: '', reason: 'lightest' }` → aucune décision
 * valide n'en découle, jamais de crash de rendu.
 *
 * La `reason` distingue le cas « plus léger a gagné » (`lightest`) du cas où le départage
 * `isCanonical` a tranché une égalité de taille (`canonical`) — pour afficher pourquoi ce
 * chemin est conservé.
 */
export function pickDefaultKeep(paths: readonly ExactDupPath[]): DefaultKeep {
  if (paths.length === 0) return { relPath: '', reason: 'lightest' };

  // Comparateur total déterministe : taille ASC, puis indexé d'abord, puis chemin court,
  // puis lexicographique. Le premier élément après tri est le keeper.
  const sorted = [...paths].sort((a, b) => {
    if (a.sizeBytes !== b.sizeBytes) return a.sizeBytes - b.sizeBytes;
    if (a.isCanonical !== b.isCanonical) return a.isCanonical ? -1 : 1;
    if (a.relPath.length !== b.relPath.length) return a.relPath.length - b.relPath.length;
    return a.relPath < b.relPath ? -1 : a.relPath > b.relPath ? 1 : 0;
  });

  const winner = sorted[0]!;
  // La RAISON : si un AUTRE chemin est strictement plus léger, on n'aurait pas choisi
  // celui-ci — donc « plus léger » est bien la raison. Sinon (le gagnant est parmi les
  // plus légers), c'est le départage qui a tranché ; on l'attribue à « copie indexée »
  // seulement si le gagnant EST indexé (sinon on retombe sur « plus léger » : la taille
  // minimale reste la cause dominante affichable).
  const minSize = winner.sizeBytes;
  const tiesForMin = paths.filter((p) => p.sizeBytes === minSize);
  const reason: ExactKeepReason =
    tiesForMin.length > 1 && winner.isCanonical ? 'canonical' : 'lightest';
  return { relPath: winner.relPath, reason };
}

export interface ExactCopiesSectionProps {
  libraryId: string;
  /** Page contrôlée (0-based). Non fourni → état interne. */
  page?: number;
  onPageChange?: (page: number) => void;
  /** Choix contrôlés (`identityHash` → `keepRelPath`). Non fourni → état interne. */
  choices?: ExactChoices;
  onChoicesChange?: (choices: ExactChoices) => void;
  /**
   * Ouvre une image dans la visionneuse. Fourni par le parent pour ÉCRIRE la réserve de
   * défilement AVANT de naviguer (scroll-restore). Absent → `openViewerSeries` direct.
   */
  onOpenViewer?: (assetIds: string[], startId: string) => void;
  /**
   * Notifie le parent que la page de groupes est CHARGÉE (`ready`) — gate la restauration
   * du défilement (`useScrollRestore`). Optionnel.
   */
  onReadyChange?: (ready: boolean) => void;
}

export function ExactCopiesSection({
  libraryId,
  page: pageProp,
  onPageChange,
  choices: choicesProp,
  onChoicesChange,
  onOpenViewer,
  onReadyChange,
}: ExactCopiesSectionProps): React.JSX.Element {
  const { t, tp } = useI18n();
  const navigate = useNavigate();

  const [applyBusy, setApplyBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // État interne de repli quand la page/les choix ne sont pas contrôlés par le parent.
  const [pageState, setPageState] = useState(0);
  const [choicesState, setChoicesState] = useState<ExactChoices>(new Map());
  const page = pageProp ?? pageState;
  const choices = choicesProp ?? choicesState;

  const setPage = useCallback(
    (next: number | ((p: number) => number)) => {
      const resolve = (p: number) => (typeof next === 'function' ? next(p) : next);
      if (onPageChange) onPageChange(resolve(page));
      else setPageState((p) => resolve(p));
    },
    [onPageChange, page],
  );

  const setChoices = useCallback(
    (next: ExactChoices | ((c: ExactChoices) => ExactChoices)) => {
      const resolve = (c: ExactChoices) => (typeof next === 'function' ? next(c) : next);
      if (onChoicesChange) onChoicesChange(resolve(choices));
      else setChoicesState((c) => resolve(c));
    },
    [onChoicesChange, choices],
  );

  const limit = EXACT_DUP_LIMIT_DEFAULT;
  const offset = page * limit;

  const groupsState = useExactGroups(libraryId, limit, offset, refreshKey);
  const summaryState = useExactSummary(libraryId, refreshKey);

  const groups = groupsState.data?.groups;

  // `setChoices` n'a PAS d'identité stable en mode contrôlé (elle dépend de `choices`).
  // On garde la dernière version dans une ref pour que l'effet d'amorçage NE dépende QUE de
  // `groups` (sans re-déclencher à chaque changement de choix, ce qui bouclerait).
  const setChoicesRef = useRef(setChoices);
  setChoicesRef.current = setChoices;

  // Auto-rafraîchissement APRÈS la fin du job de suppression (`dedup_exact_apply`).
  // Recharger juste après l'enfilage renverrait les MÊMES chemins (job pas encore fini)
  // → on attend la fin du job (front descendant ou repli par délai) pour recharger.
  const onJobFinished = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setChoicesRef.current(new Map());
    setFeedback({ kind: 'applyQueued' });
  }, []);
  const {
    active: eraseActive,
    pending: erasePending,
    markPending,
  } = useRefreshAfterJob(libraryId, 'dedup_exact_apply', onJobFinished);
  // La suppression est en cours tant que le job n'est pas fini (armé OU observé actif).
  const eraseInProgress = erasePending || eraseActive;

  // Signale au parent l'état « prêt » (groupes chargés) → gate de la restauration scroll.
  useEffect(() => {
    onReadyChange?.(groupsState.status === 'ready');
  }, [groupsState.status, onReadyChange]);

  // Amorce le choix par défaut de chaque groupe fraîchement chargé (sans écraser un choix
  // déjà présent — ex. restauré depuis la réserve). Défaut = chemin le plus léger.
  useEffect(() => {
    if (!groups) return;
    setChoicesRef.current((prev) => {
      let mutated = false;
      const next = new Map(prev);
      for (const group of groups) {
        if (next.has(group.identityHash)) continue;
        next.set(group.identityHash, pickDefaultKeep(group.paths).relPath);
        mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [groups]);

  const setChoice = useCallback(
    (identityHash: string, keepRelPath: string) => {
      setChoices((prev) => {
        const next = new Map(prev);
        next.set(identityHash, keepRelPath);
        return next;
      });
    },
    [setChoices],
  );

  // Décisions dérivées des choix, sur les groupes de la PAGE COURANTE uniquement
  // (l'application porte sur ce qui est visible ; changer de page avant d'appliquer ne
  // soumet PAS les pages précédentes — jamais de suppression « hors champ de vision »).
  const decisions = useMemo<ExactDupDecision[]>(() => {
    if (!groups) return [];
    return groups.map((g) => ({
      identityHash: g.identityHash,
      keepRelPath: choices.get(g.identityHash) ?? pickDefaultKeep(g.paths).relPath,
    }));
  }, [groups, choices]);

  // Nombre de copies supprimées = Σ (chemins − 1) sur la page (chaque groupe conserve
  // exactement 1 chemin, tous les autres sont supprimés).
  const deleteCount = useMemo(
    () => (groups ? groups.reduce((acc, g) => acc + (g.paths.length - 1), 0) : 0),
    [groups],
  );

  const onRefresh = useCallback(() => {
    setPage(0);
    setChoices(new Map());
    setFeedback(null);
    setRefreshKey((k) => k + 1);
  }, [setPage, setChoices]);

  const onConfirmApply = useCallback(async () => {
    if (!libraryId || applyBusy || decisions.length === 0) return;
    setApplyBusy(true);
    setFeedback(null);
    try {
      await applyExact(libraryId, {
        decisions: decisions as ExactDupApplyRequest['decisions'],
        dryRun: false,
      });
      recordAction('dedup.exactApply', { deleted: deleteCount });
      setConfirmOpen(false);
      // NE PAS recharger tout de suite : le job n'a pas encore supprimé les fichiers,
      // le serveur renverrait les MÊMES chemins. On arme l'observation du job et on
      // recharge à sa FIN (`useRefreshAfterJob` → `onJobFinished`).
      markPending();
      setFeedback({ kind: 'applyRunning' });
      setChoices(new Map());
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setFeedback({ kind: 'writeDisabled' });
      } else {
        setFeedback({ kind: 'applyError' });
      }
      setConfirmOpen(false);
    } finally {
      setApplyBusy(false);
    }
  }, [libraryId, applyBusy, decisions, deleteCount, setChoices, markPending]);

  const total = groupsState.data?.total ?? 0;
  const groupCount = summaryState.data?.groupCount ?? 0;
  const redundantCount = summaryState.data?.redundantCount ?? 0;
  const lastScanAt = summaryState.data?.lastScanAt ?? null;
  const hasGroups = (groups?.length ?? 0) > 0;

  return (
    <section
      aria-labelledby="exact-heading"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      {/* En-tête : titre + résumé + actualiser. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2
            id="exact-heading"
            className="flex items-center gap-2 text-base font-semibold text-fg"
          >
            <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
            {t('dedup.exact.title')}
          </h2>
          <p className="max-w-2xl text-sm text-muted">{t('dedup.exact.intro')}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-md border border-border px-4 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('dedup.exact.refresh')}
        </button>
      </div>

      <Summary groupCount={groupCount} redundantCount={redundantCount} lastScanAt={lastScanAt} />

      {feedback && <FeedbackBanner feedback={feedback} />}

      {groupsState.status === 'error' ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center" role="alert">
          <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
          <p className="text-fg">{t('dedup.exact.errorTitle')}</p>
          <button
            type="button"
            onClick={groupsState.reload}
            className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('dedup.exact.retry')}
          </button>
        </div>
      ) : groupsState.status === 'loading' || groupsState.status === 'idle' ? (
        <p className="py-10 text-center text-muted" role="status">
          {t('dedup.exact.loading')}
        </p>
      ) : hasGroups ? (
        <>
          <ul className="flex flex-col gap-4">
            {groups!.map((group) => (
              <ExactGroupCard
                key={group.identityHash}
                group={group}
                keepRelPath={
                  choices.get(group.identityHash) ?? pickDefaultKeep(group.paths).relPath
                }
                onChoose={(rel) => setChoice(group.identityHash, rel)}
                onOpen={
                  group.assetId !== null
                    ? () => {
                        const ids = [group.assetId!];
                        if (onOpenViewer) onOpenViewer(ids, group.assetId!);
                        else openViewerSeries(navigate, ids, group.assetId!, '/outils/dedup');
                      }
                    : undefined
                }
              />
            ))}
          </ul>

          <Pagination
            page={page}
            limit={limit}
            total={total}
            loaded={groups!.length}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />

          {/* Barre d'application. */}
          <div className="flex flex-col gap-2 border-t border-border pt-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                // ⚠ `eraseInProgress` : après `setChoices(new Map())`, l'effet d'amorçage
                // ré-amorce les choix des groupes encore visibles (périmés) → `deleteCount`
                // redevient > 0. Cette garde EXPLICITE empêche un double-envoi pendant la
                // fenêtre où le job tourne encore et où la liste n'est pas rechargée.
                disabled={deleteCount === 0 || applyBusy || eraseInProgress}
                onClick={() => setConfirmOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                {t('dedup.exact.applyButton')}
              </button>
              {eraseInProgress ? (
                <p className="inline-flex items-center gap-2 text-sm text-fg" role="status">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('dedup.exact.applyRunning')}
                </p>
              ) : (
                <p className="text-sm text-muted" aria-live="polite">
                  {deleteCount > 0
                    ? tp('dedup.exact.selectedForDeletion', deleteCount)
                    : t('dedup.exact.applyNone')}
                </p>
              )}
            </div>
            <p className="text-xs text-muted">{t('dedup.exact.pageScopeNote')}</p>
          </div>
        </>
      ) : (
        <EmptyState scanned={lastScanAt !== null} />
      )}

      {confirmOpen && (
        <Portal>
          <ExactConfirmModal
            count={deleteCount}
            busy={applyBusy}
            onConfirm={() => void onConfirmApply()}
            onClose={() => setConfirmOpen(false)}
          />
        </Portal>
      )}
    </section>
  );
}

/** Résumé : N groupes · M copies redondantes · inventaire à jour au … / jamais scanné. */
function Summary({
  groupCount,
  redundantCount,
  lastScanAt,
}: {
  groupCount: number;
  redundantCount: number;
  lastScanAt: string | null;
}): React.JSX.Element {
  const { t, tp, locale } = useI18n();
  const when =
    lastScanAt !== null
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(lastScanAt),
        )
      : null;

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
      {when !== null ? (
        <>
          {/* Nombre de copies redondantes EN TÊTE : en gras si non nul, discret si zéro. */}
          <span className={redundantCount > 0 ? 'font-semibold text-fg' : 'text-muted'}>
            {tp('dedup.exact.summaryRedundant', redundantCount)}
          </span>
          <span className="text-muted">{tp('dedup.exact.summaryGroups', groupCount)}</span>
          <span className="text-muted">{interpolate(t('dedup.exact.inventoryAt'), { when })}</span>
        </>
      ) : (
        <span className="font-semibold text-fg">{t('dedup.exact.neverScanned')}</span>
      )}
    </div>
  );
}

/** Bannière de retour (suppression en cours / enfilée, ou erreur). */
function FeedbackBanner({ feedback }: { feedback: Feedback }): React.JSX.Element {
  const { t } = useI18n();
  const isError = feedback.kind === 'applyError' || feedback.kind === 'writeDisabled';
  const isRunning = feedback.kind === 'applyRunning';
  const message =
    feedback.kind === 'applyRunning'
      ? t('dedup.exact.applyRunning')
      : feedback.kind === 'applyQueued'
        ? t('dedup.exact.applyQueued')
        : feedback.kind === 'writeDisabled'
          ? t('dedup.exact.errorWriteDisabled')
          : t('dedup.exact.errorApply');
  return (
    <p
      role={isError ? 'alert' : 'status'}
      className={`inline-flex items-start gap-2 rounded-md border p-3 text-sm ${
        isError ? 'border-danger/40 bg-surface-2 text-danger' : 'border-border bg-surface-2 text-fg'
      }`}
    >
      {isError ? (
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      ) : isRunning ? (
        <ArrowPathIcon
          className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-brand"
          aria-hidden="true"
        />
      ) : (
        <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
      )}
      <span>{message}</span>
    </p>
  );
}

/**
 * Carte d'un groupe de copies exactes : UNE vignette de l'image + la liste de ses
 * chemins physiques (taille, badge « Indexé », octets identiques vs ré-encodage), avec
 * un radiogroup pour choisir LE chemin à garder.
 */
function ExactGroupCard({
  group,
  keepRelPath,
  onChoose,
  onOpen,
}: {
  group: ExactDupGroup;
  keepRelPath: string;
  onChoose: (relPath: string) => void;
  /** Ouvre l'image dans la visionneuse (absent si l'asset est désindexé). */
  onOpen?: (() => void) | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const resolution =
    group.width !== null && group.height !== null
      ? interpolate(t('dedup.exact.resolution'), { width: group.width, height: group.height })
      : t('dedup.exact.resolutionUnknown');
  const kindLabel =
    group.kind === 'video' ? t('dedup.exact.kindVideo') : t('dedup.exact.kindImage');
  // Hash du chemin gardé → distingue octet-à-octet (même hash) vs ré-encodage.
  const keptHash = group.paths.find((p) => p.relPath === keepRelPath)?.fileHash ?? null;
  // Raison du DÉFAUT (« plus léger » vs « copie indexée ») — informative, jamais la couleur seule.
  const defaultKeep = pickDefaultKeep(group.paths);

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4 sm:flex-row">
      {/* Vignette (ou repère générique si l'asset a été désindexé). Cliquable → visionneuse. */}
      {group.assetId !== null && onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={interpolate(t('dedup.exact.openInViewer'), { name: group.filename })}
          className="relative h-32 w-32 shrink-0 overflow-hidden rounded-md bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <img
            src={withSlotParam(`/api/assets/${group.assetId}/thumbnail?size=256`)}
            alt={group.filename}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="h-full w-full object-cover"
          />
        </button>
      ) : (
        <div className="relative h-32 w-32 shrink-0 overflow-hidden rounded-md bg-surface-2">
          <span className="flex h-full w-full items-center justify-center text-muted">
            <PhotoIcon className="h-8 w-8" aria-hidden="true" />
            <span className="sr-only">{t('dedup.exact.noThumbnail')}</span>
          </span>
        </div>
      )}

      {/* Métadonnées + chemins. */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="break-all text-sm font-medium text-fg">{group.filename}</p>
          <p className="text-xs text-muted">
            {kindLabel} · {resolution}
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label={t('dedup.exact.chooseKeepLabel')}
          className="flex flex-col gap-2"
        >
          {group.paths.map((path) => (
            <PathRow
              key={path.relPath}
              path={path}
              identityHash={group.identityHash}
              selected={path.relPath === keepRelPath}
              sameBytesAsKept={keptHash !== null && path.fileHash === keptHash}
              keepReason={
                path.relPath === keepRelPath && path.relPath === defaultKeep.relPath
                  ? defaultKeep.reason
                  : null
              }
              onSelect={() => onChoose(path.relPath)}
              locale={locale}
            />
          ))}
        </div>
      </div>
    </li>
  );
}

/** Une ligne chemin : radio de sélection + taille + badges (Indexé, octets/ré-encodage). */
function PathRow({
  path,
  identityHash,
  selected,
  sameBytesAsKept,
  keepReason,
  onSelect,
  locale,
}: {
  path: ExactDupPath;
  identityHash: string;
  selected: boolean;
  sameBytesAsKept: boolean;
  /** Raison du choix par défaut si CE chemin est le keeper par défaut (sinon `null`). */
  keepReason: ExactKeepReason | null;
  onSelect: () => void;
  locale: Parameters<typeof formatBytes>[1];
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    // `relative` : cette radio de chemin gardé est VISIBLE (pas `sr-only`), donc elle ne
    // gonfle pas le document ; on ancre malgré tout le label par cohérence/sûreté du focus
    // (no-op visuel sans décalage) — même famille de garde que `DecisionRadio` et le corps
    // défilant de `DedupReviewPage`.
    <label
      className={`relative flex items-start gap-2 rounded-md border p-2 text-sm ${
        selected ? 'border-brand bg-brand/5' : 'border-border'
      }`}
    >
      <input
        type="radio"
        name={`keep-${identityHash}`}
        checked={selected}
        onChange={onSelect}
        className="mt-0.5 h-4 w-4 border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-all text-fg" title={path.relPath}>
          {path.relPath}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
          <span>{formatBytes(path.sizeBytes, locale)}</span>
          {/* Octets identiques vs ré-encodage (pixels identiques) : icône + texte. */}
          <span className="inline-flex items-center gap-1">
            {sameBytesAsKept ? (
              <DocumentDuplicateIcon className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Square2StackIcon className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {sameBytesAsKept ? t('dedup.exact.identicalBytes') : t('dedup.exact.reencoded')}
          </span>
          {path.isCanonical && (
            <span className="inline-flex items-center gap-1 rounded bg-surface-2 px-1.5 py-0.5 font-medium text-fg">
              <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t('dedup.exact.canonical')}
            </span>
          )}
          {/* État conservé/supprimé + RAISON du défaut (icône + texte, jamais la couleur seule). */}
          {selected ? (
            <span className="inline-flex items-center gap-1 font-medium text-fg">
              {t('dedup.exact.willKeep')}
              {keepReason !== null && (
                <span className="inline-flex items-center gap-1 text-muted">
                  <ScaleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {keepReason === 'lightest'
                    ? t('dedup.exact.keptLightest')
                    : t('dedup.exact.keptCanonical')}
                </span>
              )}
            </span>
          ) : (
            <span className="font-medium text-fg">{t('dedup.exact.willDelete')}</span>
          )}
        </span>
      </span>
    </label>
  );
}

/** Pagination précédent/suivant des groupes de copies exactes. */
function Pagination({
  page,
  limit,
  total,
  loaded,
  onPrev,
  onNext,
}: {
  page: number;
  limit: number;
  total: number;
  loaded: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element | null {
  const { t } = useI18n();
  if (total <= limit) return null;
  const from = page * limit + 1;
  const to = page * limit + loaded;
  const hasPrev = page > 0;
  const hasNext = to < total;

  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        disabled={!hasPrev}
        onClick={onPrev}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('dedup.exact.prevPage')}
      </button>
      <p className="text-sm text-muted" aria-live="polite">
        {interpolate(t('dedup.exact.pageStatus'), { from, to, total })}
      </p>
      <button
        type="button"
        disabled={!hasNext}
        onClick={onNext}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('dedup.exact.nextPage')}
      </button>
    </div>
  );
}

/** État vide : « jamais scanné » vs « aucune copie exacte trouvée ». */
function EmptyState({ scanned }: { scanned: boolean }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center text-muted">
      <DocumentDuplicateIcon className="h-10 w-10" aria-hidden="true" />
      <p className="font-medium text-fg">
        {scanned ? t('dedup.exact.emptyNoCopies') : t('dedup.exact.emptyNeverScanned')}
      </p>
    </div>
  );
}

// --- Chargement de données (hooks locaux, miroir de DedupReviewPage) ----------

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

function useExactGroups(
  libraryId: string,
  limit: number,
  offset: number,
  refreshKey: number,
): {
  status: LoadStatus;
  data: Awaited<ReturnType<typeof fetchExactGroups>> | null;
  reload: () => void;
} {
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchExactGroups>> | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!libraryId) {
      controllerRef.current?.abort();
      setStatus('idle');
      setData(null);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('loading');
    fetchExactGroups(libraryId, limit, offset, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [libraryId, limit, offset, refreshKey, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { status, data, reload };
}

function useExactSummary(
  libraryId: string,
  refreshKey: number,
): { data: Awaited<ReturnType<typeof fetchExactSummary>> | null } {
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchExactSummary>> | null>(null);

  useEffect(() => {
    if (!libraryId) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    fetchExactSummary(libraryId, controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setData(d);
      })
      .catch(() => {
        /* résumé optionnel : silencieux (l'écran fonctionne sans) */
      });
    return () => controller.abort();
  }, [libraryId, refreshKey]);

  return { data };
}
